/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import styled from 'styled-components';
import { chartConfigUtilService } from './chartConfigUtilService';

const ChartContainer = styled.div`
    width: 100%;
    height: 100%;
    min-height: 250px;
`;

// Y-axis label formatter - formats large numbers with 'k' suffix
function yAxisLabelFormatter(this: Highcharts.AxisLabelsFormatterContextObject): string {
    const value = this.value as number;
    if (value >= 1000) {
        return `${value / 1000}k`;
    }
    return String(value);
}

export interface AreaChartSeriesItem {
    name: string;
    data: number[];
    color: string;
    fillColor?: string;
    fillOpacity?: number;
}

export interface AreaChartProps {
    /** Series data for the area chart */
    series: AreaChartSeriesItem[];
    /** Categories for the x-axis (e.g., dates) */
    categories: string[];
    /** Chart height in pixels */
    height?: number;
    /** Y-axis title */
    yAxisTitle?: string;
    /** Show legend */
    showLegend?: boolean;
    /** Additional Highcharts options to merge */
    additionalConfig?: Highcharts.Options;
}

export const AreaChart: React.FC<AreaChartProps> = ({
    series,
    categories,
    height = 300,
    yAxisTitle = '',
    showLegend = true,
    additionalConfig = {},
}) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<Highcharts.Chart | null>(null);

    useEffect(() => {
        if (chartRef.current && series.length > 0) {
            const seriesData: Highcharts.SeriesOptionsType[] = series.map((s) => ({
                type: 'area' as const,
                name: s.name,
                data: s.data,
                color: s.color,
                fillColor: s.fillColor || chartConfigUtilService.convertColorToLowerOpacity(s.color, 0.15),
                lineWidth: 2,
                marker: {
                    enabled: false,
                    symbol: 'circle',
                    radius: 3,
                    states: {
                        hover: {
                            enabled: true,
                            radius: 5,
                        },
                    },
                },
            }));

            const config = chartConfigUtilService.createMultilineAreaChartConfig(seriesData, {
                chart: {
                    height,
                    type: 'area',
                    spacing: [10, 10, 10, 10],
                },
                xAxis: {
                    categories,
                    tickmarkPlacement: 'on',
                    labels: {
                        style: {
                            fontSize: '12px',
                            fontWeight: '300',
                            color: 'rgba(98, 100, 105, 1)',
                        },
                    },
                },
                yAxis: {
                    title: {
                        text: yAxisTitle,
                    },
                    labels: {
                        formatter: yAxisLabelFormatter,
                    },
                },
                legend: {
                    enabled: showLegend,
                },
                plotOptions: {
                    area: {
                        stacking: undefined,
                        lineWidth: 2,
                        marker: {
                            enabled: false,
                        },
                        fillOpacity: 0.15,
                    },
                },
                ...additionalConfig,
            });

            chartInstanceRef.current = Highcharts.chart(chartRef.current, config);
        }

        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [series, categories, height, yAxisTitle, showLegend, additionalConfig]);

    return <ChartContainer className="c-area-chart" ref={chartRef} />;
};

export default AreaChart;
